import { AlertTriangle, ChevronDown, ChevronRight, ExternalLink, Info, Settings } from "lucide-react";
import React, { startTransition,Suspense, useCallback, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation, useRefetchableFragment } from "react-relay";
import { ROOTLY_ALLOWED_INTEGRATIONS, SUPERADMIN_ONLY_INTEGRATIONS } from "src/components/integrations/integration-categories";
import { ScopeGate } from "src/components/ScopeGate";
import { Badge } from "src/components/ui/badge";
import { Button } from "src/components/ui/button";
import { Card } from "src/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import IntegrationIcon from "src/components/ui/IntegrationIcon";
import { LoadingSpinner } from "src/components/ui/loading-spinner";
import { Tooltip } from "src/components/ui/tooltip";
import { UITourBanner } from "src/components/ui/UITourBanner";
import { usePermissions } from "src/context/MeContext";
import { useToast } from "src/hooks/use-toast";
import { getProductName, isRootlyBranded, isSlackManagedByRootly, shouldShowAlertsOnly } from "src/utils/brandTheme";

import { IntegrationProvider, useIntegrationContext } from "../context/IntegrationContext";
import { IntegrationsSection_organization$key,IntegrationStatusType, IntegrationType } from "./__generated__/IntegrationsSection_organization.graphql";
import type { IntegrationsSectionCreateGitHubPermissionUpdateLinkMutation } from "./__generated__/IntegrationsSectionCreateGitHubPermissionUpdateLinkMutation.graphql";
import BuildkiteIntegration from "./BuildkiteIntegration";
import CloudWatchIntegrationLazy from "./CloudWatchIntegration-lazy";
import CloudWatchIntegrationsDialogLazy from "./CloudWatchIntegrationsDialog-lazy";
import ConfluenceIntegration from "./ConfluenceIntegration";
import DatadogInstructions from "./DatadogInstructionsContent";
import DatadogIntegration from "./DatadogIntegration";
import DatadogIntegrationsDialog from "./DatadogIntegrationsDialog";
import DeleteIntegrationButton from "./DeleteIntegrationButton";
import DiscordIntegration from "./DiscordIntegration";
import FireHydrantIntegration from "./FireHydrantIntegration";
import GcpCloudMonitoringInstructions from "./GcpCloudMonitoringInstructionsContent";
import GcpCloudMonitoringIntegration from "./GcpCloudMonitoringIntegration";
import GithubIntegration from "./GithubIntegration";
import GitHubIntegrationsDialog from "./GitHubIntegrationsDialog";
import GitLabIntegration from "./GitLabIntegration";
import GitLabIntegrationsDialog from "./GitLabIntegrationsDialog";
import GrafanaCloudInstructions from "./GrafanaCloudInstructionsContent";
import GrafanaCloudIntegration from "./GrafanaCloudIntegration";
import IncidentIoIntegration from "./IncidentIoIntegration";
import IntegrationForm from "./IntegrationForm";
import LangSmithIntegrationLazy from "./LangSmithIntegration-lazy";
import NewRelicIntegration from "./NewRelicIntegration";
import NotionIntegrationLazy from "./NotionIntegration-lazy";
import OpsgenieIntegrationLazy from "./OpsgenieIntegration-lazy";
import PagerDutyIntegrationLazy from "./PagerDutyIntegration-lazy";
import PGAnalyzeIntegration from "./PGAnalyzeIntegration";
import { RollbarInstructions } from "./RollbarIntegration";
import RollbarIntegrationLazy from "./RollbarIntegration-lazy";
import RollbarIntegrationsDialog from "./RollbarIntegrationsDialog";
import RootlyIntegration from "./RootlyIntegration";
import SentryIntegrationLazy from "./SentryIntegration-lazy";
import { SettingsPageHeader } from "./SettingsPageHeader";
import SlackIntegration from "./SlackIntegration";
import TemporalIntegration from "./TemporalIntegration";
import TemporalIntegrationForm from "./TemporalIntegrationForm";

/**
 * Title-cases a string by capitalizing the first letter of each word.
 */
function formatIntegrationName(name: string): string {
  return name
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Maps integration type to display name for consistent naming across the UI.
 */
const INTEGRATION_TYPE_DISPLAY_NAMES: Record<string, string> = {
  BUILDKITE: "Buildkite",
  CLOUDWATCH: "AWS CloudWatch",
  CONFLUENCE: "Confluence",
  DATADOG: "Datadog",
  DISCORD: "Discord",
  FIREHYDRANT: "FireHydrant",
  GCP_CLOUD_MONITORING: "GCP Cloud Monitoring",
  GITHUB: "GitHub",
  GITLAB: "GitLab",
  GRAFANA_CLOUD: "Grafana",
  LANGSMITH: "LangSmith",
  NEW_RELIC: "New Relic",
  NOTION: "Notion",
  OPSGENIE: "Opsgenie",
  PAGERDUTY: "PagerDuty",
  PROMETHEUS: "Prometheus",
  ROLLBAR: "Rollbar",
  ROOTLY: "Rootly",
  SENTRY: "Sentry",
  SLACK: "Slack",
  TEMPORAL: "Temporal",
};

/**
 * Gets the display name for an integration, using the type mapping if available.
 */
function getIntegrationDisplayName(integrationType: string, fallbackName: string): string {
  return INTEGRATION_TYPE_DISPLAY_NAMES[integrationType] || formatIntegrationName(fallbackName);
}

// Refetchable Fragment for IntegrationsSection
const integrationsSectionFragment = graphql`
  fragment IntegrationsSection_organization on GraphQLOrganization
  @refetchable(queryName: "IntegrationsSectionRefetchQuery") {
    brandTheme
    name
    hasPendingGithubInstallation
    integrations {
      edges {
        node {
          id
          name
          integrationType
          integrationStatus
        }
      }
    }
    notificationIntegrations(kind: DISCORD_WEBHOOK) {
      edges {
        node {
          id
        }
      }
    }
    slackbotInviteLink
  }
`;

// New query for fetching integration credentials
const integrationCredentialsQuery = graphql`
  query IntegrationsSectionCredentialsQuery($id: GlobalID!) {
    node(id: $id) {
      ... on GraphQLIntegration {
        id
        credentials
        createdAt
      }
    }
  }
`;

// Mutation for creating GitHub permission update link
const createGitHubPermissionUpdateLinkMutation = graphql`
  mutation IntegrationsSectionCreateGitHubPermissionUpdateLinkMutation {
    createGithubPermissionUpdateLink {
      permissionUpdateLink
    }
  }
`;


interface IntegrationsSectionProps {
  organization: IntegrationsSection_organization$key;
  isSuperAdmin?: boolean;
}

interface IntegrationCardProps {
  integration: {
    name: string;
    type: IntegrationType | string;
    isConnected: boolean;
    isPending?: boolean;
    id?: string;
    enabled?: boolean;
    integrationStatus?: IntegrationStatusType | null;
  };
  onManage: (integration: any) => void;
}



// GitHub Permission Approval Banner Component
const GitHubPermissionApprovalBanner = () => {
  const [commitCreatePermissionLink] = useMutation<IntegrationsSectionCreateGitHubPermissionUpdateLinkMutation>(createGitHubPermissionUpdateLinkMutation);
  const [isLoading, setIsLoading] = useState(false);

  const handleApprovePermissions = () => {
    setIsLoading(true);

    commitCreatePermissionLink({
      variables: {},
      onCompleted: (response) => {
        setIsLoading(false);
        if (response.createGithubPermissionUpdateLink?.permissionUpdateLink) {
          // Open the GitHub permission approval link in a new tab
          window.open(response.createGithubPermissionUpdateLink.permissionUpdateLink, "_blank");
        }
      },
      onError: (error) => {
        setIsLoading(false);
        console.error("Failed to create GitHub permission update link:", error);
      },
    });
  };

  return (
    <div className="mb-4 p-4 bg-orange-50/10 border border-orange-200/20 rounded-lg">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 mt-0.5">
          <AlertTriangle className="w-5 h-5 text-orange-400" />
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-orange-400 mb-1">
            GitHub Permissions Need Approval
          </h4>
          <p className="text-sm text-orange-200/80 mb-3">
            Your GitHub App permissions have been updated and need approval to take effect.
          </p>
          <Button
            onClick={handleApprovePermissions}
            disabled={isLoading}
            variant="default"
            size="sm"
          >
            {isLoading ? (
              <>
                <LoadingSpinner className="w-4 h-4 mr-2" />
                Loading...
              </>
            ) : (
              <>
                <ExternalLink className="w-4 h-4 mr-2" />
                Approve Permissions
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// New Relic-specific instructions component
const NewRelicInstructions: React.FC<{ productName: string }> = ({ productName }) => {
  return (
    <div className="text-muted-foreground space-y-3">
      <div className="mb-4">
        <p className="mb-2 font-bold">API Key Instructions:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>
            Log into your New Relic account.
          </li>
          <li>
            Navigate to <span className="text-amber-600">Administration</span>
            {" > "}
            <span className="text-amber-600">API keys</span> in the main menu.
          </li>
          <li>
            Click on <span className="text-amber-600">Create a key</span>.
          </li>
          <li>
            Select <span className="text-amber-600">User</span> as the key type.
          </li>
          <li>
            Enter a name for your key (e.g., "{productName} Integration") and click{" "}
            <span className="text-amber-600">Create key</span>.
          </li>
          <li>
            Copy the generated API Key and paste it into the API Key field below.
          </li>
        </ol>
      </div>

      <div className="mb-4">
        <p className="mb-2 font-bold">Account ID Instructions:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>
            Your account ID can be found in your New Relic account URL, e.g.{" "}
            <span className="font-mono text-green-400">https://one.newrelic.com/nr1-core?account=ACCOUNT_ID</span>.
          </li>
          <li>
            Copy your account ID and paste it into the Account ID field below.
          </li>
        </ol>
      </div>

      <div className="mb-4">
        <p className="mb-2 font-bold">Region Configuration:</p>
        <ul className="text-sm space-y-1 list-disc list-inside">
          <li>
            For US region: Use <span className="font-mono text-green-400">US</span>
          </li>
          <li>
            For EU region: Use <span className="font-mono text-green-400">EU</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

// Buildkite-specific instructions component
const BuildkiteInstructions: React.FC<{ productName: string }> = ({ productName }) => {
  return (
    <div className="text-muted-foreground space-y-3">
      <div className="mb-4">
        <p className="mb-2 font-bold">API Token Instructions:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>
            Log into your Buildkite account.
          </li>
          <li>
            Navigate to <span className="text-amber-600">Personal Settings</span>
            {" > "}
            <span className="text-amber-600">API Access Tokens</span>.
          </li>
          <li>
            Click on <span className="text-amber-600">New API Access Token</span>.
          </li>
          <li>
            Enter a description for your token (e.g., "{productName} Integration").
          </li>
          <li>
            Select the organization you want to connect.
          </li>
          <li>
            Select all <span className="text-amber-600">"read_"</span> scopes.
          </li>
          <li>
            Click <span className="text-amber-600">Create New API Access Token</span>.
          </li>
          <li>
            Copy the generated token and paste it into the API Token field below.
          </li>
        </ol>
      </div>

      <div className="mb-4">
        <p className="mb-2 font-bold">Organization Slug Instructions:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>
            Your organization slug can be found in your Buildkite URL, e.g.{" "}
            <span className="font-mono text-green-400">https://buildkite.com/ORGANIZATION_SLUG</span>.
          </li>
          <li>
            Copy your organization slug and paste it into the Organization Slug field below.
          </li>
        </ol>
      </div>
    </div>
  );
};

// Temporal-specific instructions component
const TemporalInstructions: React.FC<{ authType?: string }> = ({ authType }) => {
  if (authType === "MTLS") {
    return (
      <div className="text-muted-foreground space-y-3">
        <div className="mb-4">
          <p className="mb-2 font-bold">Certificate (mTLS) Instructions:</p>
          <ol className="text-sm space-y-2 list-decimal list-inside">
            <li>
              Generate a client certificate and private key (PEM) for the namespace you want to
              connect. See the{" "}
              <a
                href="https://docs.temporal.io/cloud/certificates"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:text-primary/80 underline"
              >
                Manage certificates guide
              </a>
              .
            </li>
            <li>
              In the Temporal Cloud UI, open the target namespace and add the CA certificate so
              it trusts the client cert.
            </li>
            <li>
              From the namespace overview page, copy the namespace name (e.g.{" "}
              <code>my-ns.acct</code>) and the gRPC endpoint (e.g.{" "}
              <code>my-ns.acct.tmprl.cloud:7233</code>).
            </li>
            <li>
              <strong>Note:</strong> Temporal Cloud certificates are scoped to a single namespace.
            </li>
          </ol>
        </div>
      </div>
    );
  }
  return (
    <div className="text-muted-foreground space-y-3">
      <div className="mb-4">
        <p className="mb-2 font-bold">API Key Instructions:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>
            Log into your Temporal Cloud account.
          </li>
          <li>
            Create an account-level API key by following the{" "}
            <a
              href="https://docs.temporal.io/cloud/api-keys#generate-an-api-key"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary/80 underline"
            >
              API Key generation guide
            </a>
            . The key must have permission to list namespaces.
          </li>
          <li>
            Copy the generated API key and paste it into the API Key field below.
          </li>
        </ol>
      </div>
    </div>
  );
};

// Sentry-specific instructions component
const SentryInstructions: React.FC<{ productName: string }> = ({ productName }) => {
  return (
    <div className="text-muted-foreground space-y-3">
      <div className="mb-4">
        <p className="mb-2 font-bold">Organization Slug Setup:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>
            Navigate to <span className="text-amber-600">Settings</span>
            {" > "}
            <span className="text-amber-600">Organization</span>
            {" > "}
            <span className="text-amber-600">General Settings</span> to find your organization slug.
          </li>
          <li>
            Copy your organization slug and paste it into the Organization Slug field below.
          </li>
        </ol>
      </div>

      <div className="mb-4">
        <p className="mb-2 font-bold">Auth Token Setup:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>
            Log into your Sentry account.
          </li>
          <li>
            Navigate to <span className="text-amber-600">Settings</span>
            {" > "}
            <span className="text-amber-600">Developer Settings</span>
            {" > "}
            <span className="text-amber-600">Custom Integrations</span>.
          </li>
          <li>
            Click on <span className="text-amber-600">Create New Integration</span>.
          </li>
          <li>
            Select <span className="text-amber-600">Internal Integration</span>.
          </li>
          <li>
            Enter a name for your integration (e.g., "{productName} Integration").
          </li>
          <li>
            Select the following permissions:
            <ul className="ml-6 mt-1 list-disc">
              <li><span className="text-amber-600">Project: Read</span></li>
              <li><span className="text-amber-600">Team: Read</span></li>
              <li><span className="text-amber-600">Issue & Event: Read</span></li>
              <li><span className="text-amber-600">Organization: Read</span></li>
              <li><span className="text-amber-600">Member: Read</span></li>
              <li><span className="text-amber-600">Alerts: Read</span></li>
            </ul>
          </li>
          <li>
            Click <span className="text-amber-600">Save Changes</span>.
          </li>
          <li>
            On the Edit Internal Integration page, scroll to the <span className="text-amber-600">Tokens</span> section.
          </li>
          <li>
            Click <span className="text-amber-600">New Token</span>.
          </li>
          <li>
            Copy the generated token and paste it into the Auth Token field below.
          </li>
        </ol>
      </div>
    </div>
  );
};

// CloudWatch-specific instructions component
const CloudWatchInstructions: React.FC<{ productName: string }> = ({ productName }) => {
  return (
    <div className="text-muted-foreground space-y-3">
      <div className="mb-4">
        <p className="mb-2 font-bold">IAM Role Setup:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>
            Log into your AWS Console and navigate to{" "}
            <span className="text-amber-600">IAM</span> {" > "}
            <span className="text-amber-600">Roles</span>.
          </li>
          <li>
            Click <span className="text-amber-600">Create role</span>.
          </li>
          <li>
            Select <span className="text-amber-600">Custom trust policy</span> as the trusted entity type.
          </li>
          <li>
            Paste the trust policy (with your organization's External ID) and click{" "}
            <span className="text-amber-600">Next</span>.
          </li>
          <li>
            Search for and attach the <span className="text-amber-600">CloudWatchReadOnlyAccess</span> AWS managed policy.
          </li>
          <li>
            Click <span className="text-amber-600">Next</span> and give your role a name (e.g., "{productName}-CloudWatch-Access").
          </li>
          <li>
            Click <span className="text-amber-600">Create role</span>.
          </li>
          <li>
            Copy the <span className="text-amber-600">Role ARN</span> from the role details page.
          </li>
        </ol>
      </div>
    </div>
  );
};

// LangSmith-specific instructions component
const LangSmithInstructions: React.FC<{ productName: string }> = ({ productName }) => {
  return (
    <div className="text-muted-foreground space-y-3">
      <div className="mb-4">
        <p className="mb-2 font-bold">API Key Setup:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>
            Log into your{" "}
            <a
              href="https://smith.langchain.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber-600 hover:underline"
            >
              LangSmith account
            </a>
          </li>
          <li>
            Navigate to <span className="text-amber-600">Settings</span>
            {" > "}
            <span className="text-amber-600">API Keys</span>
          </li>
          <li>
            Click <span className="text-amber-600">+ API Key</span>
          </li>
          <li>
            Configure the key:
            <ul className="ml-6 mt-1 list-disc">
              <li>Description: <span className="text-amber-600">{productName}</span></li>
              <li>Key Type: <span className="text-amber-600">Service Key</span></li>
              <li>Workspaces: <span className="text-amber-600">Specific Workspaces</span> (select the single workspace you want to integrate)
                <p className="text-sm text-yellow-500 mt-1">Note: Only a single workspace is supported at this time.</p>
              </li>
            </ul>
          </li>
          <li>
            Click <span className="text-amber-600">Create API Key</span>
          </li>
          <li>
            Copy the key and paste it into the API Key field below.
          </li>
        </ol>
      </div>

      <div className="mb-4">
        <p className="mb-2 font-bold">Workspace ID Setup:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>
            Navigate to <span className="text-amber-600">Settings</span>
            {" > "}
            <span className="text-amber-600">Workspaces</span>
          </li>
          <li>
            Select the workspace you want to integrate
          </li>
          <li>
            In the header, hover over <span className="text-amber-600">ID</span> and click to copy
          </li>
          <li>
            Paste the Workspace ID into the field below.
          </li>
        </ol>
      </div>
    </div>
  );
};

// Notion-specific instructions component
const NotionInstructions: React.FC<{ productName: string }> = ({ productName }) => {
  return (
    <div className="text-muted-foreground space-y-3">
      <div className="mb-4">
        <p className="mb-2 font-bold">API Key Instructions:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>
            Log into your Notion account at{" "}
            <a
              href="https://www.notion.so/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary/80 underline"
            >
              https://www.notion.so/
            </a>
          </li>
          <li>
            Navigate to <span className="text-amber-600">Settings</span>
            {" > "}
            <span className="text-amber-600">Connections</span>
          </li>
          <li>
            Click on <span className="text-amber-600">Develop or manage integrations</span>
          </li>
          <li>
            Click on <span className="text-amber-600">New integration</span>
          </li>
          <li>
            Enter a name for your integration (e.g., "{productName} Integration")
          </li>
          <li>
            Select the appropriate 'Associated workspace' and 'Internal' as the 'Type'. Then click{" "}
            <span className="text-amber-600">Save</span>
          </li>
          <li>
            Under Capabilities, select 'Read content', 'Read comments', and 'Read user information including email addresses'.
            Then click <span className="text-amber-600">Save</span>
          </li>
          <li>
            Copy the Internal Integration Secret and paste it into the API Key field below
          </li>
          <li>
            In the Notion Integration page, click on <span className="text-amber-600">Access</span> (next to the "Configuration" tab)
          </li>
          <li>
            Select the appropriate workspace or pages to give access to the integration and click{" "}
            <span className="text-amber-600">Update access</span>
          </li>
        </ol>
      </div>
    </div>
  );
};

// IntegrationIcon has been moved to its own component file

// Loading placeholder for integration details
const IntegrationDetailsLoading = () => (
  <div className="space-y-3">
    <div className="h-6 bg-accent rounded-md animate-pulse w-1/2"></div>
    <div className="h-32 bg-accent rounded-md animate-pulse"></div>
    <div className="h-6 bg-accent rounded-md animate-pulse w-3/4"></div>
    <div className="h-6 bg-accent rounded-md animate-pulse w-2/3"></div>
  </div>
);

// Integration details component
const IntegrationDetails = ({
  integration,
  credentials,
  createdAt,
  productName,
  onClose
}: {
  integration: IntegrationCardProps["integration"],
  credentials: any,
  createdAt?: string,
  productName: string,
  onClose?: () => void
}) => {
  const [showInstructions, setShowInstructions] = React.useState(false);

  const capitalizedName = formatIntegrationName(integration.name);

  // Format the date for display
  const formattedDate = createdAt ? new Date(createdAt).toLocaleDateString() : null;

  // Check if this is a GitHub integration that needs permission approval
  const isGitHubIntegration = integration.type === "GITHUB";
  const isDatadogIntegration = integration.type === "DATADOG";
  const isNewRelicIntegration = integration.type === "NEW_RELIC";
  const isBuildkiteIntegration = integration.type === "BUILDKITE";
  const isTemporalIntegration = integration.type === "TEMPORAL";
  const isGrafanaCloudIntegration = integration.type === "GRAFANA_CLOUD";
  const isGcpCloudMonitoringIntegration = integration.type === "GCP_CLOUD_MONITORING";
  const isRollbarIntegration = integration.type === "ROLLBAR";
  const isSentryIntegration = integration.type === "SENTRY";
  const isNotionIntegration = integration.type === "NOTION";
  const isCloudWatchIntegration = integration.type === "CLOUDWATCH";
  const isLangSmithIntegration = integration.type === "LANGSMITH";
  const needsPermissionApproval = isGitHubIntegration &&
    credentials &&
    credentials.permissions_approval_needed === true;

  // Get credential fields for display
  const credentialFields = credentials ? Object.entries(credentials).filter(([key]) =>
    !key.startsWith("_") && key !== "permissions_approval_needed"
  ) : [];

  // For Grafana Cloud, ensure all datasource UID fields are always displayed
  if (isGrafanaCloudIntegration && credentials) {
    const requiredFields = ["endpoint", "api_token", "loki_datasource_uid", "prometheus_datasource_uid", "tempo_datasource_uid"];
    const existingKeys = new Set(credentialFields.map(([key]) => key));

    // Add missing datasource UID fields with placeholder value
    requiredFields.forEach(field => {
      if (!existingKeys.has(field)) {
        credentialFields.push([field, null]);
      }
    });

    // Sort to maintain consistent order
    const fieldOrder = ["endpoint", "api_token", "loki_datasource_uid", "prometheus_datasource_uid", "tempo_datasource_uid"];
    credentialFields.sort((a, b) => {
      const indexA = fieldOrder.indexOf(a[0]);
      const indexB = fieldOrder.indexOf(b[0]);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  }

  // For Temporal, match the order shown in the connect form (auth_type → API key OR
  // namespace fields → cert → private key → optional root CA).
  if (isTemporalIntegration && credentials) {
    const isMtls = credentials.auth_type === "MTLS";
    const requiredFields = isMtls
      ? ["auth_type", "namespace", "namespace_grpc_endpoint", "client_cert_pem", "client_private_key_pem", "server_root_ca_cert_pem"]
      : ["auth_type", "api_key"];
    const existingKeys = new Set(credentialFields.map(([key]) => key));
    requiredFields.forEach(field => {
      if (!existingKeys.has(field)) {
        credentialFields.push([field, null]);
      }
    });
    credentialFields.sort((a, b) => {
      const indexA = requiredFields.indexOf(a[0]);
      const indexB = requiredFields.indexOf(b[0]);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  }

  return (
    <div className="space-y-6 overflow-y-auto max-h-[80vh] p-1">
      {/* GitHub Permission Approval Banner */}
      {needsPermissionApproval && (
        <GitHubPermissionApprovalBanner />
      )}

      {/* Datadog Instructions Toggle */}
      {isDatadogIntegration && (
        <div className="space-y-3">
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
          >
            {showInstructions ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            {showInstructions ? "Hide instructions" : "Show instructions"}
          </button>

          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            showInstructions
              ? "max-h-[2000px] opacity-100"
              : "max-h-0 opacity-0"
          }`}>
            <DatadogInstructions productName={productName} />
          </div>
        </div>
      )}

      {/* New Relic Instructions Toggle */}
      {isNewRelicIntegration && (
        <div className="space-y-3">
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
          >
            {showInstructions ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            {showInstructions ? "Hide instructions" : "Show instructions"}
          </button>

          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            showInstructions
              ? "max-h-[2000px] opacity-100"
              : "max-h-0 opacity-0"
          }`}>
            <NewRelicInstructions productName={productName} />
          </div>
        </div>
      )}

      {/* Buildkite Instructions Toggle */}
      {isBuildkiteIntegration && (
        <div className="space-y-3">
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
          >
            {showInstructions ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            {showInstructions ? "Hide instructions" : "Show instructions"}
          </button>

          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            showInstructions
              ? "max-h-[2000px] opacity-100"
              : "max-h-0 opacity-0"
          }`}>
            <BuildkiteInstructions productName={productName} />
          </div>
        </div>
      )}

      {/* Temporal Instructions Toggle */}
      {isTemporalIntegration && (
        <div className="space-y-3">
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
          >
            {showInstructions ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            {showInstructions ? "Hide instructions" : "Show instructions"}
          </button>

          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            showInstructions
              ? "max-h-[2000px] opacity-100"
              : "max-h-0 opacity-0"
          }`}>
            <TemporalInstructions authType={credentials?.auth_type} />
          </div>
        </div>
      )}

      {/* GCP Cloud Monitoring Instructions Toggle */}
      {isGcpCloudMonitoringIntegration && (
        <div className="space-y-3">
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
          >
            {showInstructions ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            {showInstructions ? "Hide instructions" : "Show instructions"}
          </button>

          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            showInstructions
              ? "max-h-[2000px] opacity-100"
              : "max-h-0 opacity-0"
          }`}>
            <GcpCloudMonitoringInstructions productName={productName} />
          </div>
        </div>
      )}

      {/* Sentry Instructions Toggle */}
      {isSentryIntegration && (
        <div className="space-y-3">
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
          >
            {showInstructions ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            {showInstructions ? "Hide instructions" : "Show instructions"}
          </button>

          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            showInstructions
              ? "max-h-[2000px] opacity-100"
              : "max-h-0 opacity-0"
          }`}>
            <SentryInstructions productName={productName} />
          </div>
        </div>
      )}

      {/* Rollbar Instructions Toggle */}
      {isRollbarIntegration && (
        <div className="space-y-3">
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
          >
            {showInstructions ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            {showInstructions ? "Hide instructions" : "Show instructions"}
          </button>

          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            showInstructions
              ? "max-h-[2000px] opacity-100"
              : "max-h-0 opacity-0"
          }`}>
            <RollbarInstructions productName={productName} />
          </div>
        </div>
      )}

      {/* Grafana Cloud Instructions Toggle */}
      {isGrafanaCloudIntegration && (
        <div className="space-y-3">
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
          >
            {showInstructions ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            {showInstructions ? "Hide instructions" : "Show instructions"}
          </button>

          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            showInstructions
              ? "max-h-[2000px] opacity-100"
              : "max-h-0 opacity-0"
          }`}>
            <GrafanaCloudInstructions productName={productName} />
          </div>
        </div>
      )}

      {/* Notion Instructions Toggle */}
      {isNotionIntegration && (
        <div className="space-y-3">
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
          >
            {showInstructions ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            {showInstructions ? "Hide instructions" : "Show instructions"}
          </button>

          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            showInstructions
              ? "max-h-[2000px] opacity-100"
              : "max-h-0 opacity-0"
          }`}>
            <NotionInstructions productName={productName} />
          </div>
        </div>
      )}

      {/* CloudWatch Instructions Toggle */}
      {isCloudWatchIntegration && (
        <div className="space-y-3">
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
          >
            {showInstructions ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            {showInstructions ? "Hide instructions" : "Show instructions"}
          </button>

          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            showInstructions
              ? "max-h-[2000px] opacity-100"
              : "max-h-0 opacity-0"
          }`}>
            <CloudWatchInstructions productName={productName} />
          </div>
        </div>
      )}

      {/* LangSmith Instructions Toggle */}
      {isLangSmithIntegration && (
        <div className="space-y-3">
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
          >
            {showInstructions ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            {showInstructions ? "Hide instructions" : "Show instructions"}
          </button>

          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            showInstructions
              ? "max-h-[2000px] opacity-100"
              : "max-h-0 opacity-0"
          }`}>
            <LangSmithInstructions productName={productName} />
          </div>
        </div>
      )}

      <div className="space-y-6">
        {isTemporalIntegration ? (
          <div className="border-t border-border pt-4">
            <TemporalIntegrationForm
              initialCredentials={credentials}
              integrationId={integration.id || undefined}
              onComplete={(success) => {
                if (success) {
                  onClose?.();
                  window.location.reload();
                }
              }}
              onCancel={() => onClose?.()}
              leftSlot={
                <DeleteIntegrationButton
                  integrationId={integration.id || ""}
                  integrationName={capitalizedName}
                  onSuccess={() => window.location.reload()}
                />
              }
            />
            {formattedDate && (
              <p className="text-xs text-muted-foreground mt-3">Connected on: {formattedDate}</p>
            )}
          </div>
        ) : (
        <div className="border-t border-border pt-4">
          <h3 className="text-base font-semibold text-foreground mb-4">
            {isGitHubIntegration ? "GitHub App Configuration" : isGcpCloudMonitoringIntegration ? "Service Account Configuration" : isCloudWatchIntegration ? "AWS Configuration" : "API Key Configuration"}
          </h3>

          <div className="space-y-4">
            {credentialFields.map(([key, value]) => {
              // For GitHub, only show account_name and installation_id
              if (isGitHubIntegration && key !== "account_name" && key !== "installation_id") {
                return null;
              }

              // Hide org_subdomain for all integrations
              if (key === "org_subdomain") {
                return null;
              }

              // Hide internal `type` field added by AWS_SM writes (used by GHA scripts only)
              if (key === "type") {
                return null;
              }

              // Hide test_page_url for Notion (used for validation only)
              if (key === "test_page_url") {
                return null;
              }

              // Hide name for Notion (auto-generated)
              if (key === "name" && integration.type === "NOTION") {
                return null;
              }

              // Hide project_id for GCP (projects are discovered automatically)
              if (key === "project_id" && isGcpCloudMonitoringIntegration) {
                return null;
              }

              // Format label properly for specific keys
              let label = key.replace(/_/g, " ");
              if (key === "api_key") {
                label = "API Key";
              } else if (key === "api_token") {
                label = "API Token";
              } else if (key === "app_key") {
                label = "Application Key";
              } else if (key === "account_name") {
                label = "Account Name";
              } else if (key === "installation_id") {
                label = "Installation ID";
              } else if (key === "loki_datasource_uid") {
                label = "Loki Datasource UID";
              } else if (key === "prometheus_datasource_uid") {
                label = "Prometheus Datasource UID";
              } else if (key === "tempo_datasource_uid") {
                label = "Tempo Datasource UID";
              } else if (key === "service_account_email") {
                label = "Service Account Email";
              } else if (key === "project_id") {
                label = "Project ID";
              } else if (key === "role_arn" || key === "aws_iam_role_arn") {
                label = "AWS IAM Role ARN";
              } else if (key === "external_id") {
                label = "External ID";
              } else if (key === "region" || key === "aws_region") {
                label = "AWS Region";
              } else if (key === "auth_type") {
                label = "Authentication Type";
              } else if (key === "namespace_grpc_endpoint") {
                label = "Namespace gRPC Endpoint";
              } else if (key === "client_cert_pem") {
                label = "Client Certificate (PEM)";
              } else if (key === "client_private_key_pem") {
                label = "Client Private Key (PEM)";
              } else if (key === "server_root_ca_cert_pem") {
                label = "Server Root CA Certificate (PEM)";
              } else {
                label = formatIntegrationName(label);
              }

              // For Grafana Cloud datasource UIDs, show "<Not Connected>" if empty
              const isGrafanaDatasourceUid = isGrafanaCloudIntegration &&
                (key === "loki_datasource_uid" || key === "prometheus_datasource_uid" || key === "tempo_datasource_uid");
              const isOptionalTemporalField = isTemporalIntegration && key === "server_root_ca_cert_pem";
              const isEmpty = value === null || value === undefined || value === "";
              const displayValue = isEmpty && isGrafanaDatasourceUid
                ? "<Not Connected>"
                : isEmpty && isOptionalTemporalField
                  ? "<Not set>"
                  : typeof value === "object"
                    ? JSON.stringify(value)
                    : String(value);

              const isPemField = key === "client_cert_pem" || key === "client_private_key_pem" || key === "server_root_ca_cert_pem";

              return (
                <div key={key} className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    {label}
                  </label>
                  {isPemField ? (
                    <textarea
                      value={displayValue}
                      disabled
                      rows={6}
                      className="flex w-full rounded-md border border-border bg-card text-foreground px-3 py-2 text-xs font-mono opacity-60 cursor-not-allowed resize-none"
                    />
                  ) : (
                    <input
                      type="text"
                      value={displayValue}
                      disabled
                      className="flex h-10 w-full rounded-md border border-border bg-card text-foreground px-3 py-2 text-sm opacity-60 cursor-not-allowed"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        )}
      </div>

      {!isTemporalIntegration && (
        <div className="flex justify-between items-center pt-2">
          {formattedDate && (
            <div className="text-xs text-muted-foreground">
              Connected on: {formattedDate}
            </div>
          )}
          <div className="flex space-x-3">
            {/* GitHub: Show Manage button that links to GitHub app settings */}
            {isGitHubIntegration && credentials && credentials.account_name && credentials.installation_id ? (
              <Button
                variant="outline"
                onClick={() => {
                  const settingsUrl = `https://github.com/organizations/${credentials.account_name}/settings/installations/${credentials.installation_id}`;
                  window.open(settingsUrl, "_blank");
                }}
              >
                <Settings className="w-4 h-4 mr-2" />
                Manage
              </Button>
            ) : (
              /* Non-GitHub integrations: Show Disconnect button */
              <DeleteIntegrationButton
                integrationId={integration.id || ""}
                integrationName={capitalizedName}
                onSuccess={() => window.location.reload()}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Read-only banner component
const ReadOnlyIntegrationsBanner = () => {
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");

  if (canModify) {
    return null;
  }

  return (
    <div className="mb-6 flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/10 p-4">
      <Info className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm text-primary">
          You have read-only access to integrations. Contact an admin to make changes.
        </p>
      </div>
    </div>
  );
};

const getIntegrationTypeLabel = (type: IntegrationType | string): string => {
  switch (type) {
    case "GITHUB":
    case "GITLAB":
      return "code";
    case "SLACK":
    case "DISCORD":
      return "messaging";
    case "PAGERDUTY":
    case "OPSGENIE":
    case "ROOTLY":
    case "FIREHYDRANT":
      return "on-call";
    case "NOTION":
    case "CONFLUENCE":
      return "documentation";
    case "BUILDKITE":
      return "cicd";
    case "TEMPORAL":
      return "workflow";
    case "DATADOG":
    case "NEW_RELIC":
    case "GRAFANA_CLOUD":
    case "PROMETHEUS":
    case "CLOUDWATCH":
    case "OPENSEARCH":
    case "ROLLBAR":
    case "SENTRY":
    default:
      return "telemetry";
  }
};

const IntegrationCard = ({ integration, onManage }: IntegrationCardProps) => {
  const { name, type, isConnected, isPending } = integration;
  // Check if integration is disabled (enabled === false)
  const isDisabled = "enabled" in integration && integration.enabled === false;
  const typeLabel = getIntegrationTypeLabel(type);
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");

  const renderStatus = () => {
    if (isConnected) {
      return (
        <div className={`flex items-center text-xs mt-1 ${
          integration.integrationStatus === "PENDING" ? "text-yellow-500" : "text-green-500"
        }`}>
          <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
            integration.integrationStatus === "PENDING" ? "bg-yellow-500 animate-pulse" : "bg-green-500"
          }`}></div>
          {integration.integrationStatus === "APPROVED" ? "Connected" :
            integration.integrationStatus === "PENDING" ? "Pending" : "Connected"}
        </div>
      );
    }
    if (isPending) {
      return (
        <div className="flex items-center text-xs mt-1 text-yellow-500">
          <div className="w-1.5 h-1.5 rounded-full bg-yellow-500 animate-pulse mr-1.5"></div>
          Pending Admin Approval
        </div>
      );
    }
    if (isDisabled) {
      return (
        <div className="flex items-center text-xs text-muted-foreground mt-1">
          <div className="w-1.5 h-1.5 rounded-full bg-neutral-400 mr-1.5"></div>
          Not available for your organization
        </div>
      );
    }
    return null;
  };

  return (
    <Card
      interactive={!isDisabled}
      className={`p-5 bg-transparent ${isDisabled ? "opacity-60" : ""}`}
    >
      <div className="flex items-center space-x-4 mb-4">
        <div className="flex-shrink-0">
          <IntegrationIcon type={type} />
        </div>
        <div className="flex-grow">
          <h3 className="font-medium text-foreground">{name}</h3>
          {renderStatus()}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <Badge variant="outline" size="xs" className="text-muted-foreground border-border">
          #{typeLabel}
        </Badge>
        {isConnected ? (
          <ScopeGate scope="write:integrations">
            <Button
              variant="outline"
              size="sm"
              className="min-w-24 border-border text-muted-foreground hover:text-foreground hover:bg-card/50"
              onClick={() => onManage(integration)}
            >
              Manage
            </Button>
          </ScopeGate>
        ) : isPending ? (
          <Button
            variant="outline"
            size="sm"
            className="min-w-24 border-yellow-600 text-yellow-500 hover:text-yellow-400 hover:bg-yellow-950/30"
            onClick={() => onManage(integration)}
          >
            View Status
          </Button>
        ) : (
          <Tooltip
            content={!canModify ? "You don't have permission to connect integrations" : null}
            position="top"
          >
            <span className="inline-block">
              <Button
                variant="primary"
                size="sm"
                className="min-w-24"
                onClick={() => onManage(integration)}
                disabled={isDisabled || !canModify}
              >
                {isDisabled ? "Not available" : "Connect"}
              </Button>
            </span>
          </Tooltip>
        )}
      </div>
    </Card>
  );
};

// Component for the integration credentials dialog
// This is loaded lazily when the user clicks "Manage"
function IntegrationCredentialsDialogContent({
  integration,
  isOpen,
  onClose,
  brandTheme
}: {
  integration: IntegrationCardProps["integration"] | null,
  isOpen: boolean,
  onClose: () => void,
  brandTheme?: string | null
}) {
  const [detailsLoaded, setDetailsLoaded] = useState(false);
  const { setCredentials } = useIntegrationContext();
  const productName = getProductName(brandTheme);

  // Only fetch credentials if dialog is open and we have an integration ID
  const fetchCredentials = integration?.id && isOpen;

  // Fetch the credentials for this integration
  const data = fetchCredentials ? useLazyLoadQuery(
    integrationCredentialsQuery,
    { id: integration.id },
    { fetchPolicy: "network-only" } // Always fetch fresh data
  ) : null;

  // Simulate loading with a short delay
  React.useEffect(() => {
    if (isOpen) {
      setDetailsLoaded(false);
      const timer = setTimeout(() => {
        setDetailsLoaded(true);
      }, 800);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen, integration?.id]);

  // Extract credentials and createdAt from the response
  // Type guard to check if data has a node property
  interface DataWithNode {
    node?: {
      credentials?: any;
      createdAt?: string;
    };
  }

  // Check if data is not null and has the expected shape
  const isDataWithNode = (data: any): data is DataWithNode => {
    return data !== null && typeof data === "object" && "node" in data;
  };

  const credentials = isDataWithNode(data) ? data.node?.credentials : null;
  const createdAt = isDataWithNode(data) ? data.node?.createdAt : null;

  // Store the credentials for potential edit later
  React.useEffect(() => {
    if (credentials) {
      setCredentials(credentials);
    }
  }, [credentials, setCredentials]);

  // No edit functionality

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl bg-popover text-foreground border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <IntegrationIcon type={integration?.type} className="h-6 w-6" />
            Connect {integration?.name}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Connect your {integration?.name} account to give {productName} access.
          </DialogDescription>
        </DialogHeader>

        {/* Lazy loaded content */}
        {!detailsLoaded || !integration ? (
          <IntegrationDetailsLoading />
        ) : (
          <IntegrationDetails
            integration={integration}
            credentials={credentials}
            createdAt={createdAt || undefined}
            productName={productName}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// Wrapper component that provides the context - exported for use in onboarding
export function IntegrationCredentialsDialog(props: {
  integration: IntegrationCardProps["integration"] | null,
  isOpen: boolean,
  onClose: () => void,
  brandTheme?: string | null
}) {
  return (
    <IntegrationProvider>
      <IntegrationCredentialsDialogContent {...props} />
    </IntegrationProvider>
  );
}

export default function IntegrationsSection({ organization }: IntegrationsSectionProps) {
  const [organizationData, refetch] = useRefetchableFragment(integrationsSectionFragment, organization);
  const { toast } = useToast();
  const { isSuperAdmin } = usePermissions();
  const brandTheme = organizationData.brandTheme ?? "TIERZERO";
  const productName = getProductName(brandTheme);
  const shouldApplyWhitelist = isRootlyBranded(brandTheme) && brandTheme !== "ROOTLY_HYBRID";

  // Show toast for post-redirect integration connect errors
  React.useEffect(() => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      const confluenceError = urlParams.get("confluence_error");

      if (confluenceError) {
        toast({
          title: "Confluence Integration Failed",
          description: `Failed to connect Confluence: ${confluenceError}`,
          variant: "error",
        });
        // Remove url param
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete("confluence_error");
        window.history.replaceState({}, "", newUrl.toString());
      }
    }
  }, [toast]);

  // Helper function to render integration dialogs
  const renderIntegrationDialog = () => {
    if (!showIntegrationForm || !selectedIntegration) return null;

    const integrationType = selectedIntegration.type;

    const dialogProps = {
      isOpen: showIntegrationForm,
      onClose: handleFormCancel,
      brandTheme,
    };

    // Custom integration dialogs
    const customIntegrations = {
      SLACK: <SlackIntegration {...dialogProps} slackbotInviteLink={organizationData.slackbotInviteLink} isConnected={selectedIntegration.isConnected} />,
      DISCORD: <DiscordIntegration {...dialogProps} />,
      GITHUB: selectedIntegration.isConnected
        ? <GitHubIntegrationsDialog {...dialogProps} onRefetch={() => refetch({}, { fetchPolicy: "network-only" })} />
        : <GithubIntegration {...dialogProps} />,
      GITLAB: selectedIntegration.isConnected
        ? <GitLabIntegrationsDialog {...dialogProps} onRefetch={() => refetch({}, { fetchPolicy: "network-only" })} />
        : <GitLabIntegration {...dialogProps} />,
      CONFLUENCE: <ConfluenceIntegration {...dialogProps} />,
      FIREHYDRANT: <FireHydrantIntegration {...dialogProps} />,
      ROOTLY: <RootlyIntegration {...dialogProps} />,
      OPSGENIE: (
        <Suspense fallback={<Dialog open={showIntegrationForm} onOpenChange={handleFormCancel}><DialogContent className="sm:max-w-4xl bg-popover text-foreground border-border"><div className="flex justify-center p-8"><LoadingSpinner className="h-6 w-6" /></div></DialogContent></Dialog>}>
          <OpsgenieIntegrationLazy {...dialogProps} />
        </Suspense>
      ),
      NOTION: (
        <Suspense fallback={<Dialog open={showIntegrationForm} onOpenChange={handleFormCancel}><DialogContent className="sm:max-w-4xl bg-popover text-foreground border-border"><div className="flex justify-center p-8"><LoadingSpinner className="h-6 w-6" /></div></DialogContent></Dialog>}>
          <NotionIntegrationLazy {...dialogProps} />
        </Suspense>
      ),
      PAGERDUTY: (
        <Suspense fallback={<Dialog open={showIntegrationForm} onOpenChange={handleFormCancel}><DialogContent className="sm:max-w-4xl bg-popover text-foreground border-border"><div className="flex justify-center p-8"><LoadingSpinner className="h-6 w-6" /></div></DialogContent></Dialog>}>
          <PagerDutyIntegrationLazy {...dialogProps} />
        </Suspense>
      ),
      DATADOG: selectedIntegration.isConnected
        ? <DatadogIntegrationsDialog {...dialogProps} onRefetch={() => refetch({}, { fetchPolicy: "network-only" })} />
        : <DatadogIntegration {...dialogProps} />,
      NEW_RELIC: !selectedIntegration.isConnected ? <NewRelicIntegration {...dialogProps} /> : null,
      BUILDKITE: !selectedIntegration.isConnected ? <BuildkiteIntegration {...dialogProps} /> : null,
      CLOUDWATCH: selectedIntegration.isConnected
        ? <CloudWatchIntegrationsDialogLazy {...dialogProps} onRefetch={() => refetch({}, { fetchPolicy: "network-only" })} />
        : <CloudWatchIntegrationLazy {...dialogProps} onRefetch={() => refetch({}, { fetchPolicy: "network-only" })} />,
      ROLLBAR: selectedIntegration.isConnected
        ? <RollbarIntegrationsDialog {...dialogProps} onRefetch={() => refetch({}, { fetchPolicy: "network-only" })} />
        : <RollbarIntegrationLazy {...dialogProps} />,
      SENTRY: <SentryIntegrationLazy {...dialogProps} />,
      LANGSMITH: <LangSmithIntegrationLazy {...dialogProps} />,
      GRAFANA_CLOUD: !selectedIntegration.isConnected ? <GrafanaCloudIntegration {...dialogProps} /> : null,
      TEMPORAL: !selectedIntegration.isConnected ? <TemporalIntegration {...dialogProps} /> : null,
      GCP_CLOUD_MONITORING: !selectedIntegration.isConnected ? <GcpCloudMonitoringIntegration {...dialogProps} /> : null,
      INCIDENT_IO: !selectedIntegration.isConnected ? <IncidentIoIntegration {...dialogProps} /> : null,
      PGANALYZE: <PGAnalyzeIntegration {...dialogProps} />,
    };

    const CustomDialog = customIntegrations[integrationType as keyof typeof customIntegrations];

    if (CustomDialog) {
      return <IntegrationProvider>{CustomDialog}</IntegrationProvider>;
    }

    // Generic dialog for other integrations
    return (
      <IntegrationProvider>
        <Dialog open={showIntegrationForm} onOpenChange={handleFormCancel}>
          <DialogContent className="sm:max-w-md lg:max-w-lg bg-popover text-foreground border-border">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <IntegrationIcon type={selectedIntegration.type} className="h-6 w-6" />
                Connect {selectedIntegration.name}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                {getIntegrationHelperText(selectedIntegration.type)}
              </DialogDescription>
            </DialogHeader>
            <IntegrationForm
              integrationType={selectedIntegration.type}
              onComplete={handleFormComplete}
              onCancel={handleFormCancel}
            />
          </DialogContent>
        </Dialog>
      </IntegrationProvider>
    );
  };

  const connectedIntegrations = organizationData.integrations.edges.map((edge: any) => edge.node);

  // Check if we're coming from onboarding and get the step number
  const [onboardingStep, setOnboardingStep] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      const stepParam = urlParams.get("onboarding");
      if (stepParam) {
        const step = parseInt(stepParam, 10);
        setOnboardingStep(isNaN(step) ? null : step);
      }

      // Check if we should auto-open an integration dialog
      const integrationParam = urlParams.get("integration");
      const actionParam = urlParams.get("action");

      if (integrationParam && actionParam === "manage") {
        // Find the integration by type
        const integrationType = integrationParam.toUpperCase();
        const connectedIntegration = connectedIntegrations.find(
          (int: any) => int.integrationType === integrationType
        );

        if (connectedIntegration) {
          // Auto-open the integration dialog
          const formattedIntegration = {
            name: getIntegrationDisplayName(connectedIntegration.integrationType, connectedIntegration.name),
            type: connectedIntegration.integrationType,
            isConnected: true,
            id: connectedIntegration.id,
            integrationStatus: connectedIntegration.integrationStatus,
          };

          setSelectedIntegration(formattedIntegration);
          setShowIntegrationForm(true);
          setActiveTab("connected");

          // Clean up URL params
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete("integration");
          newUrl.searchParams.delete("action");
          window.history.replaceState({}, "", newUrl.toString());
        }
      }
    }
  }, [connectedIntegrations]);

  const isFromOnboarding = onboardingStep !== null;
  const [activeTab, setActiveTab] = useState("connected");
  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationCardProps["integration"] | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showIntegrationForm, setShowIntegrationForm] = useState(false);

  // Special case: Show Rootly integration for Prolific and Rootly organizations
  const shouldShowRootly = React.useMemo(() => {
    const orgName = organizationData.name?.toLowerCase() || "";
    return orgName === "prolific" || orgName === "rootly";
  }, [organizationData.name]);

  // Group GitHub integrations to show as single card with count
  const githubIntegrations = connectedIntegrations.filter((i: any) => i.integrationType === "GITHUB");
  const hasMultipleGitHub = githubIntegrations.length > 1;

  // Group GitLab integrations to show as single card with count
  const gitlabIntegrations = connectedIntegrations.filter((i: any) => i.integrationType === "GITLAB");
  const hasMultipleGitLab = gitlabIntegrations.length > 1;

  // Group Datadog integrations to show as single card with count
  const datadogIntegrations = connectedIntegrations.filter((i: any) => i.integrationType === "DATADOG");
  const hasMultipleDatadog = datadogIntegrations.length > 1;

  // Group CloudWatch integrations to show as single card with count
  const cloudWatchIntegrations = connectedIntegrations.filter((i: any) => i.integrationType === "CLOUDWATCH");
  const hasMultipleCloudWatch = cloudWatchIntegrations.length > 1;

  // Group Rollbar integrations to show as single card with count
  const rollbarIntegrations = connectedIntegrations.filter((i: any) => i.integrationType === "ROLLBAR");
  const hasMultipleRollbar = rollbarIntegrations.length > 1;

  // List of all connected integrations (from GraphQL)
  const connectedIntegrationsWithFormatting = connectedIntegrations
    .filter((integration: any) => {
      // Hide superadmin-only integrations from non-superadmin users
      if (!isSuperAdmin && SUPERADMIN_ONLY_INTEGRATIONS.includes(integration.integrationType)) {
        return false;
      }

      // For ROOTLY/ROOTLY_COBRAND, only show whitelisted integration types
      if (shouldApplyWhitelist) {
        if (!ROOTLY_ALLOWED_INTEGRATIONS.includes(integration.integrationType)) {
          return false;
        }
        // Hide ROOTLY integration for non-Prolific/Rootly orgs with alerts-only brand themes
        if (integration.integrationType === "ROOTLY" && !shouldShowRootly && shouldShowAlertsOnly(brandTheme)) {
          return false;
        }
        // For Slack integration, only show if the name is exactly "Slack" (TierZero bot on Rootly cobrand)
        if (integration.integrationType === "SLACK" && integration.name !== "Slack") {
          return false;
        }
      }

      // Filter out all but first GitHub integration if there are multiple
      if (integration.integrationType === "GITHUB" && hasMultipleGitHub) {
        return integration.id === githubIntegrations[0].id;
      }
      // Filter out all but first GitLab integration if there are multiple
      if (integration.integrationType === "GITLAB" && hasMultipleGitLab) {
        return integration.id === gitlabIntegrations[0].id;
      }
      // Filter out all but first Datadog integration if there are multiple
      if (integration.integrationType === "DATADOG" && hasMultipleDatadog) {
        return integration.id === datadogIntegrations[0].id;
      }
      // Filter out all but first CloudWatch integration if there are multiple
      if (integration.integrationType === "CLOUDWATCH" && hasMultipleCloudWatch) {
        return integration.id === cloudWatchIntegrations[0].id;
      }
      // Filter out all but first Rollbar integration if there are multiple
      if (integration.integrationType === "ROLLBAR" && hasMultipleRollbar) {
        return integration.id === rollbarIntegrations[0].id;
      }
      return true;
    })
    .map((integration: any) => {
      let formattedName = getIntegrationDisplayName(integration.integrationType, integration.name);

      // Add "(Managed on Rootly)" suffix for Slack integrations in ROOTLY/ROOTLY_COBRAND themes
      if (integration.integrationType === "SLACK" && isSlackManagedByRootly(brandTheme)) {
        formattedName = "Slack (Managed on Rootly)";
      }

      return {
        name: formattedName,
        type: integration.integrationType,
        isConnected: true,
        id: integration.id,
        integrationStatus: integration.integrationStatus,
        hasMultipleInstallations: (integration.integrationType === "GITHUB" && hasMultipleGitHub) || (integration.integrationType === "GITLAB" && hasMultipleGitLab) || (integration.integrationType === "DATADOG" && hasMultipleDatadog) || (integration.integrationType === "CLOUDWATCH" && hasMultipleCloudWatch) || (integration.integrationType === "ROLLBAR" && hasMultipleRollbar),
      };
    });

  // List of all available integration types
  // For Rootly branded orgs, only show whitelisted integrations
  const allIntegrationTypes = [
    { name: "AWS CloudWatch", type: "CLOUDWATCH" },
    { name: "Buildkite", type: "BUILDKITE" },
    { name: "Confluence", type: "CONFLUENCE" },
    { name: "Datadog", type: "DATADOG" },
    { name: "Discord", type: "DISCORD" },
    { name: "FireHydrant", type: "FIREHYDRANT" },
    { name: "GCP Cloud Monitoring", type: "GCP_CLOUD_MONITORING" },
    { name: "GitHub", type: "GITHUB" },
    { name: "GitLab", type: "GITLAB" },
    { name: "Grafana", type: "GRAFANA_CLOUD" },
    { name: "New Relic", type: "NEW_RELIC" },
    { name: "Notion", type: "NOTION" },
    { name: "Opsgenie", type: "OPSGENIE" },
    { name: "PagerDuty", type: "PAGERDUTY" },
    { name: "Rollbar", type: "ROLLBAR" },
    { name: "Rootly", type: "ROOTLY" },
    { name: "Sentry", type: "SENTRY" },
    { name: isSlackManagedByRootly(brandTheme) ? "Slack (Managed on Rootly)" : "Slack", type: "SLACK" },
    { name: "Temporal", type: "TEMPORAL" },
  ].filter(integration => {
    // For ROOTLY/ROOTLY_COBRAND, only show whitelisted integrations
    if (shouldApplyWhitelist && !ROOTLY_ALLOWED_INTEGRATIONS.includes(integration.type as any)) {
      return false;
    }
    // Hide ROOTLY integration for alerts-only brand themes unless org should see it
    if (integration.type === "ROOTLY" && shouldShowAlertsOnly(brandTheme) && !shouldShowRootly) {
      return false;
    }
    return true;
  });

  // Check if there's a pending GitHub installation (and no connected GitHub integrations)
  const hasGitHubConnected = githubIntegrations.length > 0;
  const hasPendingGitHub = !hasGitHubConnected && organizationData.hasPendingGithubInstallation;

  // Available integrations (those that aren't already connected)
  const availableIntegrations = allIntegrationTypes
    .filter(integration => {
      // Check standard integrations
      const hasStandardIntegration = connectedIntegrations.some((connected: any) =>
        connected.integrationType === integration.type
      );

      // Check if it's in the formatted connected list (includes simulated entries)
      const isInFormattedList = connectedIntegrationsWithFormatting.some((connected: any) =>
        connected.type === integration.type
      );

      return !hasStandardIntegration && !isInFormattedList;
    })
    .map(integration => ({
      ...integration,
      isConnected: false,
      isPending: integration.type === "GITHUB" && hasPendingGitHub
    }));

  // Handle opening the management dialog
  const handleManage = useCallback((integration: IntegrationCardProps["integration"]) => {
    setSelectedIntegration(integration);

    // Integrations that use custom dialogs instead of the generic credentials dialog
    const customDialogTypes = ["SLACK", "DISCORD", "GITHUB", "GITLAB", "DATADOG", "OPSGENIE", "PAGERDUTY", "FIREHYDRANT", "ROOTLY", "ROLLBAR"];

    if (customDialogTypes.includes(integration.type)) {
      setShowIntegrationForm(true);
    } else {
      setDialogOpen(true);
    }
  }, []);

  // Handle closing the dialog
  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    // We keep the selected integration to prevent UI flicker when closing
  }, []);

  // Handle opening the integration form dialog for a new integration
  const handleConnectIntegration = useCallback((integration: IntegrationCardProps["integration"]) => {
    // Skip if integration is disabled
    if ("enabled" in integration && integration.enabled === false) {
      return;
    }
    setSelectedIntegration(integration);
    setShowIntegrationForm(true);
  }, []);

  // Provide different helper text based on integration type
  const getIntegrationHelperText = (integrationType: IntegrationType | string) => {
    switch (integrationType) {
      case "CLOUDWATCH":
        return "Enter your AWS credentials as JSON. Required fields: aws_region, aws_iam_role_arn";
      case "CONFLUENCE":
        return "Connect your Confluence workspace to access documentation and knowledge base content";
      case "DATADOG":
        return "Enter your Datadog API and application keys as JSON. Required fields: datadog_site, api_key, app_key";
      case "FIREHYDRANT":
        return "Enter your FireHydrant API token as JSON. Required fields: api_token";
      case "NEW_RELIC":
        return "Enter your New Relic details as JSON. Required fields: account_id, api_key, region (defaults to 'US')";
      case "BUILDKITE":
        return "Enter your Buildkite API token as JSON. Required fields: organization_slug, api_token";
      case "TEMPORAL":
        return "Enter your Temporal details as JSON. Required fields: endpoint, api_key";
      case "SENTRY":
        return "Enter your Sentry details as JSON. Required fields: organization_slug, auth_token";
      case "ROLLBAR":
        return "Required fields: account_name, project_read_token. The project_name and project_id are auto-detected from the token during validation.";
      default:
        return "Enter your credentials as JSON";
    }
  };

  // Handle form completion
  const handleFormComplete = useCallback((success: boolean) => {
    setShowIntegrationForm(false);
    // Reload the page to show updated integrations
    if (success) {
      window.location.reload();
    }
  }, []);

  // Handle form cancellation
  const handleFormCancel = useCallback(() => {
    setShowIntegrationForm(false);
    // Refetch to ensure data is up to date after closing
    startTransition(() => {
      refetch({}, { fetchPolicy: "network-only" });
    });
  }, [refetch]);

  return (
    <div className="px-4 md:px-5 pt-4 pb-2 w-full">
      {/* Show onboarding banner if coming from onboarding */}
      {isFromOnboarding && (
        <UITourBanner
          text={`Connect ${productName} to your telemetry data source, code, and other external systems.`}
          className="w-full mb-6 p-4"
          showBackButton={true}
          onboardingStep={onboardingStep}
        />
      )}

      <SettingsPageHeader
        title="Integrations"
        tooltip="Connect telemetry, codebase, documents, and other data sources."
      />

      <ReadOnlyIntegrationsBanner />

      <div className="mb-6 border-b border-accent">
        <button
          className={`px-4 py-2 font-medium mr-4 ${
            activeTab === "connected"
              ? "text-cyan border-b-2 border-cyan"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setActiveTab("connected")}
        >
          Connected ({connectedIntegrationsWithFormatting.length})
        </button>
        <button
          className={`px-4 py-2 font-medium ${
            activeTab === "available"
              ? "text-cyan border-b-2 border-cyan"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setActiveTab("available")}
        >
          Available ({availableIntegrations.length})
        </button>
      </div>

      <div className="w-full">
        {activeTab === "available" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {[...availableIntegrations].sort((a, b) => {
              // First sort by availability (enabled integrations first)
              const aEnabled = !("enabled" in a) || a.enabled !== false;
              const bEnabled = !("enabled" in b) || b.enabled !== false;

              if (aEnabled !== bEnabled) {
                return aEnabled ? -1 : 1;
              }

              // Then sort alphabetically within each group
              return a.name.localeCompare(b.name);
            }).map((integration, index) => (
              <IntegrationCard
                key={`available-${integration.type}-${index}`}
                integration={integration}
                onManage={() => handleConnectIntegration(integration)}
              />
            ))}
          </div>
        )}

        {activeTab === "connected" && (
          <>
            {connectedIntegrationsWithFormatting.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {[...connectedIntegrationsWithFormatting].sort((a, b) => a.name.localeCompare(b.name)).map((integration: any, index: number) => (
                  <IntegrationCard
                    key={`connected-${integration.id || integration.type}-${index}`}
                    integration={integration}
                    onManage={handleManage}
                  />
                ))}
              </div>
            ) : (
              <div className="border border-accent rounded-lg p-8 text-center">
                <h3 className="text-lg font-medium text-muted-foreground mb-2">No connected integrations</h3>
                <p className="text-muted-foreground mb-4">Connect an integration to get started with monitoring and analytics.</p>
                <Button variant="primary" onClick={() => setActiveTab("available")}>
                  View available integrations
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Lazy-loaded dialog for integration details */}
      {dialogOpen && (
        <IntegrationCredentialsDialog
          integration={selectedIntegration}
          isOpen={dialogOpen}
          onClose={handleCloseDialog}
          brandTheme={brandTheme}
        />
      )}

      {/* Integration dialogs */}
      {renderIntegrationDialog()}
    </div>
  );
}
